import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { EnvironmentService } from '../../core/services/environment.service';

/** Response body returned by the customer validation endpoint. */
export interface CustomerValidationResponse {
  /** The customer tenant identifier. */
  customerId: string;
  /** Whether the Employee Portal is enabled for this customer. */
  portalEnabled: boolean;
  /** Whether the portal has been deactivated by an Admin (was published, now archived). */
  portalDeactivated?: boolean;
  /** Short-lived SAS URL for the customer's logo image, or null when not configured. */
  logoUrl: string | null;
  /** When true, the username/password login form should be hidden — only SSO is offered. */
  isLoginFormDisabled?: boolean;
}

/**
 * Service responsible for validating a customer access ID against the Employee Portal API.
 */
@Injectable({ providedIn: 'root' })
export class EmployeePortalConfigService {
  private readonly http = inject(HttpClient);
  private readonly envService = inject(EnvironmentService);

  /**
   * Validates the supplied customer access ID by calling the API.
   * Maps HTTP error responses to user-facing error message strings.
   * @param accessId The customer access ID entered by the user.
   * @returns An Observable of the validation response, or errors with a user-facing message.
   */
  validateCustomer(accessId: string): Observable<CustomerValidationResponse> {
    const url = `${this.envService.employeePortalApiUrl}/api/v1/auth/customer/${accessId}/validate`;
    return this.http
      .get<CustomerValidationResponse>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => this.mapError(error))));
  }

  /**
   * Validates a customer by their subdomain against the Employee Portal API.
   * The subdomain is a separate field in the database from the access ID.
   * @param subdomain The subdomain portion of the hostname (e.g. 'demo' from 'demo.localhost').
   * @returns An Observable of the validation response, or errors with a user-facing message.
   */
  validateCustomerBySubdomain(subdomain: string): Observable<CustomerValidationResponse> {
    const url = `${this.envService.employeePortalApiUrl}/api/v1/auth/customer/by-subdomain/${subdomain}`;
    return this.http
      .get<CustomerValidationResponse>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => this.mapError(error))));
  }

  private mapError(error: HttpErrorResponse): string {
    if (error.status === 404) {
      return 'The access ID you entered was not found. Please check and try again.';
    }
    return 'Something went wrong. Please try again later.';
  }
}
